import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Chip,
  Typography, IconButton, ToggleButtonGroup, ToggleButton, FormControlLabel, Switch,
  Divider, Tooltip, Snackbar, Alert, CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CodeIcon from '@mui/icons-material/Code';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SendIcon from '@mui/icons-material/Send';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  EmailTemplate, VariableDispo, parseVariables,
  majEmailTemplate, previewEmailTemplate, envoyerTestEmailTemplate
} from '../services/emailTemplatesServices';

interface Props {
  open: boolean;
  template: EmailTemplate | null;
  onClose: () => void;
  onSaved: () => void;
}

// HTML complexe (document entier / tables) : on evite l'editeur visuel qui le simplifierait
const isComplexHtml = (html: string) =>
  /<!doctype|<html|<table/i.test(html || '');

const MailEditDialog: React.FC<Props> = ({ open, template, onClose, onSaved }) => {
  const [sujet, setSujet] = useState('');
  const [corpsHtml, setCorpsHtml] = useState('');
  const [actif, setActif] = useState(true);
  const [mode, setMode] = useState<'visuel' | 'html'>('visuel');
  const [preview, setPreview] = useState<{ sujet: string; html: string }>({ sujet: '', html: '' });
  const [testEmail, setTestEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);
  const lastFocus = useRef<'sujet' | 'corps'>('corps');

  const variables: VariableDispo[] = template ? parseVariables(template.variables_dispo) : [];

  // Ré-initialisé à chaque ouverture (pas seulement au changement de template) : « Annuler » puis rouvrir le même
  // mail repart du contenu enregistré, et l'aperçu du mail précédent n'est plus affiché le temps de la requête
  useEffect(() => {
    if (!open || !template) return;
    setSujet(template.sujet || '');
    setCorpsHtml(template.corps_html || '');
    setActif(Number(template.actif) === 1);
    setMode(isComplexHtml(template.corps_html) ? 'html' : 'visuel');
    setTestEmail('');
    setPreview({ sujet: '', html: '' });
  }, [open, template]);

  // Aperçu live (debounce léger)
  useEffect(() => {
    if (!open || !template) return;
    const id = setTimeout(async () => {
      try {
        const r = await previewEmailTemplate(template.code, { sujet, corps_html: corpsHtml });
        setPreview({ sujet: r.sujet, html: r.html });
      } catch {
        /* aperçu best-effort */
      }
    }, 400);
    return () => clearTimeout(id);
  }, [sujet, corpsHtml, open, template]);

  const insererVariable = (cle: string) => {
    const token = `{{${cle}}}`;
    if (lastFocus.current === 'sujet') {
      setSujet((s) => s + token);
    } else {
      setCorpsHtml((c) => c + token);
    }
  };

  const handleSave = async () => {
    if (!template) return;
    setSaving(true);
    try {
      await majEmailTemplate(template.code, { sujet, corps_html: corpsHtml, actif });
      setToast({ msg: 'Template enregistré', sev: 'success' });
      onSaved();
      onClose();
    } catch {
      setToast({ msg: "Erreur lors de l'enregistrement", sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!template || !testEmail.trim()) return;
    setTesting(true);
    try {
      await envoyerTestEmailTemplate(template.code, { email: testEmail.trim(), sujet, corps_html: corpsHtml });
      setToast({ msg: `Mail de test envoyé à ${testEmail.trim()}`, sev: 'success' });
    } catch {
      setToast({ msg: "Erreur lors de l'envoi du test", sev: 'error' });
    } finally {
      setTesting(false);
    }
  };

  if (!template) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '92vh' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
          <Box sx={{ flexGrow: 1 }}>
            {template.libelle}
            <Typography variant="caption" display="block" color="text.secondary">
              {template.declencheur}
            </Typography>
          </Box>
          <FormControlLabel
            control={<Switch checked={actif} onChange={(e) => setActif(e.target.checked)} />}
            label={actif ? 'Actif' : 'Désactivé'}
          />
          <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ display: 'flex', gap: 2, overflow: 'hidden' }}>
          {/* Colonne édition */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflowY: 'auto', pr: 1 }}>
            <TextField
              label="Sujet"
              value={sujet}
              onChange={(e) => setSujet(e.target.value)}
              onFocus={() => (lastFocus.current = 'sujet')}
              fullWidth
              size="small"
              sx={{ mb: 1.5 }}
            />

            {variables.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Variables disponibles (cliquer pour insérer) :
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {variables.map((v) => (
                    <Tooltip key={v.cle} title={v.desc || ''} arrow>
                      <Chip
                        label={`{{${v.cle}}}`}
                        size="small"
                        onClick={() => insererVariable(v.cle)}
                        sx={{ cursor: 'pointer', fontFamily: 'monospace' }}
                      />
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="subtitle2">Corps du message</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={mode}
                onChange={(_e, v) => v && setMode(v)}
              >
                <ToggleButton value="visuel"><VisibilityIcon fontSize="small" sx={{ mr: 0.5 }} />Visuel</ToggleButton>
                <ToggleButton value="html"><CodeIcon fontSize="small" sx={{ mr: 0.5 }} />HTML</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {mode === 'visuel' ? (
              <Box
                onFocus={() => (lastFocus.current = 'corps')}
                sx={{ '& .ql-container': { minHeight: 260 }, mb: 1 }}
              >
                {isComplexHtml(corpsHtml) && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    Ce template contient une mise en page complexe (tableaux). L'édition visuelle
                    risque de la simplifier — préférez le mode HTML.
                  </Alert>
                )}
                {/* react-quill 2.0.0 rappelle onChange (source 'api') au montage avec le HTML normalisé de la valeur
                    courante — celle du mail précédent tant que l'effet d'ouverture n'est pas passé — et l'écrasait
                    après coup : seules les saisies de source 'user' sont retenues (même règle que ComposerMailDialog) */}
                <ReactQuill
                  theme="snow"
                  value={corpsHtml}
                  onChange={(html, _delta, source) => { if (source === 'user') setCorpsHtml(html); }}
                />
              </Box>
            ) : (
              <TextField
                value={corpsHtml}
                onChange={(e) => setCorpsHtml(e.target.value)}
                onFocus={() => (lastFocus.current = 'corps')}
                fullWidth
                multiline
                minRows={12}
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
              />
            )}

            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                label="Envoyer un test à"
                placeholder="email@exemple.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                size="small"
                sx={{ flexGrow: 1 }}
              />
              <Button
                variant="outlined"
                startIcon={testing ? <CircularProgress size={16} /> : <SendIcon />}
                disabled={testing || !testEmail.trim()}
                onClick={handleTest}
              >
                Test
              </Button>
            </Box>
          </Box>

          {/* Colonne aperçu */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, borderLeft: '1px solid #eee', pl: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Aperçu (données d'exemple)</Typography>
            <Box sx={{ px: 1, py: 0.5, bgcolor: '#f5f5f5', borderRadius: 1, mb: 1 }}>
              <Typography variant="caption" color="text.secondary">Sujet</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{preview.sujet || '—'}</Typography>
            </Box>
            <Box sx={{ flexGrow: 1, border: '1px solid #eee', borderRadius: 1, overflow: 'hidden' }}>
              <iframe
                title="apercu-mail"
                srcDoc={preview.html}
                style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Annuler</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </>
  );
};

export default MailEditDialog;
